import {
  ActionIcon,
  Badge,
  Box,
  Divider,
  Drawer,
  Group,
  Loader,
  Select,
  Stack,
  Switch,
  Text,
  Tooltip,
} from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useEffect, useMemo, useState } from 'react';
import { useMedplumProfile } from '@medplum/react-hooks';
import type { Practitioner } from '@medplum/fhirtypes';
import { getPractitionerOrganizationRef } from '@unify/fhir-model/resources/practitioner';
import {
  getZonedWeekdayAndMinutes,
  resolveEffectiveWeeklyHours,
  type LocationWeeklyHours,
} from '@unify/fhir-model/resources/location';
import { DEFAULT_PROVIDER_WEEKLY_AVAILABILITY_TIMEZONE } from '@unify/fhir-model/resources/schedule';
import { applyZonedWallClock } from '@/lib/unifyDateFormat';
import { useLocationResources } from '../../../hooks/useLocationResources';
import { useRoomAvailabilityForDate } from '../../../hooks/useRoomAvailabilityForDate';
import type { Room } from '../../settings/components/RoomManagementSection';
import { freeIntervalsWithinWindow, type MinuteInterval } from './roomSchedulingDemoData';
import { isRoomOpenForBooking } from './RoomSelect';

const TIMELINE_START_MIN = 7 * 60; // 7:00 AM
const TIMELINE_END_MIN = 21 * 60; // 9:00 PM
const TOTAL_MIN = TIMELINE_END_MIN - TIMELINE_START_MIN;

function denverDateKey(d: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: DEFAULT_PROVIDER_WEEKLY_AVAILABILITY_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

function closedIntervalsForHours(
  hours: LocationWeeklyHours | undefined,
  viewInstant: Date,
): MinuteInterval[] {
  const zoned = getZonedWeekdayAndMinutes(viewInstant.toISOString());
  if (!zoned) {
    return [{ startMinutes: TIMELINE_START_MIN, endMinutes: TIMELINE_END_MIN }];
  }
  const window = hours?.[zoned.day];
  if (!window || window.openingMinutes >= window.closingMinutes) {
    return [{ startMinutes: TIMELINE_START_MIN, endMinutes: TIMELINE_END_MIN }];
  }
  const closed: MinuteInterval[] = [];
  if (window.openingMinutes > TIMELINE_START_MIN) {
    closed.push({
      startMinutes: TIMELINE_START_MIN,
      endMinutes: Math.min(window.openingMinutes, TIMELINE_END_MIN),
    });
  }
  if (window.closingMinutes < TIMELINE_END_MIN) {
    closed.push({
      startMinutes: Math.max(window.closingMinutes, TIMELINE_START_MIN),
      endMinutes: TIMELINE_END_MIN,
    });
  }
  return closed.filter((c) => c.endMinutes > c.startMinutes);
}

function formatHour(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return m === 0 ? `${h12} ${period}` : `${h12}:${m.toString().padStart(2, '0')} ${period}`;
}

function formatTime(mins: number): string {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const period = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${m.toString().padStart(2, '0')} ${period}`;
}

function minutesToPct(minsFromMidnight: number): number {
  return ((minsFromMidnight - TIMELINE_START_MIN) / TOTAL_MIN) * 100;
}

function durationToPct(startMin: number, endMin: number): number {
  return ((endMin - startMin) / TOTAL_MIN) * 100;
}

function groupRooms(rooms: Room[]): { label: string; rooms: Room[] }[] {
  const therapy = rooms.filter((r) => r.type === 'therapy-room');
  const booth = rooms.filter((r) => r.type === 'phone-booth');
  const out: { label: string; rooms: Room[] }[] = [];
  if (therapy.length) out.push({ label: 'Rooms', rooms: therapy });
  if (booth.length) out.push({ label: 'Phone Booths', rooms: booth });
  return out;
}

type ResourceTimelineBooking = {
  id: string;
  startMinutes: number;
  endMinutes: number;
  providerName: string;
};

function intervalsToBookings(
  resourceId: string,
  intervals: { startMinutes: number; endMinutes: number }[],
): ResourceTimelineBooking[] {
  return intervals.map((interval, index) => ({
    id: `${resourceId}-${index}`,
    startMinutes: interval.startMinutes,
    endMinutes: interval.endMinutes,
    providerName: 'Booked',
  }));
}

const TRACK_BG =
  'repeating-linear-gradient(90deg, var(--mantine-color-gray-1) 0, var(--mantine-color-gray-1) 1px, transparent 1px, transparent calc(100% / 28))';

const HATCH_BG =
  'repeating-linear-gradient(-45deg, transparent, transparent 4px, rgba(148, 163, 184, 0.22) 4px, rgba(148, 163, 184, 0.22) 5px)';

/** Lets underlying blue availability show through appointment / conflict bands. */
const APPOINTMENT_OVERLAY_OPACITY = 0.72;

interface ResourceTimelineRowProps {
  bookings: ResourceTimelineBooking[];
  /**
   * Selected appointment: green fill when free on this resource; red fill + red border when it
   * overlaps a booking (Unify theme blue/green/red scales).
   */
  appointmentHighlight?: { startMin: number; endMin: number; conflict: boolean } | null;
  /** When true, blue bubbles represent booked (unavailable) time instead of available time. */
  inverted?: boolean;
  closedIntervals?: MinuteInterval[];
}

function ResourceTimelineRow({
  bookings,
  appointmentHighlight,
  inverted = false,
  closedIntervals = [],
}: ResourceTimelineRowProps): JSX.Element {
  const busyIntervals = useMemo(
    () => bookings.map((b) => ({ startMinutes: b.startMinutes, endMinutes: b.endMinutes })),
    [bookings],
  );
  const freeIntervals = useMemo(
    () => freeIntervalsWithinWindow(busyIntervals, TIMELINE_START_MIN, TIMELINE_END_MIN),
    [busyIntervals],
  );

  const clampedBusy = useMemo(
    () =>
      bookings
        .map((b) => ({
          start: Math.max(TIMELINE_START_MIN, b.startMinutes),
          end: Math.min(TIMELINE_END_MIN, b.endMinutes),
        }))
        .filter((b) => b.end > b.start),
    [bookings],
  );

  const displayIntervals = useMemo(() => {
    if (inverted) {
      return clampedBusy.map((b) => ({
        key: `booked-${b.start}-${b.end}`,
        startMin: b.start,
        endMin: b.end,
        tooltip: `Booked ${formatTime(b.start)} – ${formatTime(b.end)}`,
      }));
    }
    return freeIntervals.map((g) => ({
      key: `free-${g.startMinutes}-${g.endMinutes}`,
      startMin: g.startMinutes,
      endMin: g.endMinutes,
      tooltip: `Available ${formatTime(g.startMinutes)} – ${formatTime(g.endMinutes)}`,
    }));
  }, [inverted, clampedBusy, freeIntervals]);

  const clampedAppt =
    appointmentHighlight && appointmentHighlight.endMin > appointmentHighlight.startMin
      ? {
          start: Math.max(TIMELINE_START_MIN, appointmentHighlight.startMin),
          end: Math.min(TIMELINE_END_MIN, appointmentHighlight.endMin),
        }
      : null;

  return (
    <Box
      style={{
        flex: 1,
        height: 44,
        position: 'relative',
        background: TRACK_BG,
        borderRadius: 6,
        border: '1px solid var(--mantine-color-gray-3)',
      }}
    >
      {closedIntervals.map((closed) => (
        <Box
          key={`closed-${closed.startMinutes}-${closed.endMinutes}`}
          title={`Closed ${formatTime(closed.startMinutes)} – ${formatTime(closed.endMinutes)}`}
          style={{
            position: 'absolute',
            inset: 0,
            left: `${minutesToPct(closed.startMinutes)}%`,
            width: `${durationToPct(closed.startMinutes, closed.endMinutes)}%`,
            background: 'rgba(148, 163, 184, 0.32)',
            zIndex: 0,
          }}
        />
      ))}
      {!inverted &&
        clampedBusy.map((busy) => (
          <Box
            key={`busy-${busy.start}-${busy.end}`}
            style={{
              position: 'absolute',
              top: 3,
              height: 36,
              left: `${minutesToPct(busy.start)}%`,
              width: `${durationToPct(busy.start, busy.end)}%`,
              background: HATCH_BG,
              borderRadius: 4,
              zIndex: 0,
            }}
          />
        ))}
      {displayIntervals.map((interval) => (
        <Tooltip key={interval.key} label={interval.tooltip}>
          <Box
            style={{
              position: 'absolute',
              top: 3,
              height: 36,
              left: `${minutesToPct(interval.startMin)}%`,
              width: `${durationToPct(interval.startMin, interval.endMin)}%`,
              minWidth: 2,
              backgroundColor: 'var(--mantine-color-blue-1)',
              border: '1px solid var(--mantine-color-blue-6)',
              borderRadius: 4,
              zIndex: 1,
            }}
          />
        </Tooltip>
      ))}
      {clampedAppt &&
        clampedAppt.end > clampedAppt.start &&
        (appointmentHighlight?.conflict ? (
          <Box
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${minutesToPct(clampedAppt.start)}%`,
              width: `${durationToPct(clampedAppt.start, clampedAppt.end)}%`,
              minWidth: 4,
              backgroundColor: 'var(--mantine-color-red-1)',
              border: '2px solid var(--mantine-color-red-6)',
              borderRadius: 4,
              opacity: APPOINTMENT_OVERLAY_OPACITY,
              pointerEvents: 'none',
              zIndex: 3,
              boxSizing: 'border-box',
            }}
          />
        ) : (
          <Box
            style={{
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${minutesToPct(clampedAppt.start)}%`,
              width: `${durationToPct(clampedAppt.start, clampedAppt.end)}%`,
              minWidth: 4,
              backgroundColor: 'var(--mantine-color-green-1)',
              border: '2px solid var(--mantine-color-green-6)',
              borderRadius: 4,
              opacity: APPOINTMENT_OVERLAY_OPACITY,
              pointerEvents: 'none',
              zIndex: 3,
              boxSizing: 'border-box',
            }}
          />
        ))}
    </Box>
  );
}

function TimelineColorLegend({ variant, inverted = false }: { variant: 'full' | 'browse'; inverted?: boolean }): JSX.Element {
  return (
    <Stack gap={6}>
      <Group gap={6}>
        <Box
          w={14}
          h={14}
          style={{
            borderRadius: 4,
            border: '1px solid var(--mantine-color-blue-6)',
            backgroundColor: 'var(--mantine-color-blue-1)',
          }}
        />
        <Text size="xs" c="gray.6">{inverted ? 'Unavailable' : 'Available'}</Text>
      </Group>
      {variant === 'full' && (
        <>
          <Group gap={6}>
            <Box
              w={14}
              h={14}
              style={{
                borderRadius: 4,
                border: '2px solid var(--mantine-color-green-6)',
                backgroundColor: 'var(--mantine-color-green-1)',
                opacity: APPOINTMENT_OVERLAY_OPACITY,
              }}
            />
            <Text size="xs" c="gray.6">Your appointment time</Text>
          </Group>
          <Group gap={6}>
            <Box
              w={14}
              h={14}
              style={{
                borderRadius: 4,
                border: '2px solid var(--mantine-color-red-6)',
                backgroundColor: 'var(--mantine-color-red-1)',
                opacity: APPOINTMENT_OVERLAY_OPACITY,
              }}
            />
            <Text size="xs" c="gray.6">Conflict with your time</Text>
          </Group>
        </>
      )}
    </Stack>
  );
}

// ─── Shared hook: derive org ID from logged-in profile ───────────────

function useOrgId(): string {
  const profile = useMedplumProfile();
  return useMemo(() => {
    if (profile?.resourceType !== 'Practitioner') return '';
    const ref = getPractitionerOrganizationRef(profile as Practitioner);
    return ref?.reference?.replace('Organization/', '') ?? '';
  }, [profile]);
}

// ─── RoomAvailabilityDrawer ──────────────────────────────────────────

export interface RoomAvailabilityDrawerProps {
  opened: boolean;
  onClose: () => void;
  locationId: string;
  /** Day shown in the timeline */
  initialDate: Date;
  /** When set and same calendar day as viewDate, show a vertical band */
  highlightStart: Date | null;
  highlightEnd: Date | null;
  selectedRoomId: string | null;
  /** Portable equipment selected on the appointment (badges only) */
  selectedDeviceIds?: string[];
  officeSelectDisabled?: boolean;
  onOfficeLocationChange?: (locationId: string) => void;
  onPickRoom: (roomId: string) => void;
}

export function RoomAvailabilityDrawer({
  opened,
  onClose,
  locationId,
  initialDate,
  highlightStart,
  highlightEnd,
  selectedRoomId,
  selectedDeviceIds = [],
  onOfficeLocationChange,
  officeSelectDisabled = false,
  onPickRoom,
}: RoomAvailabilityDrawerProps): JSX.Element {
  const [viewDate, setViewDate] = useState(initialDate);
  const [drawerToggle, setDrawerToggle] = useState(false);

  useEffect(() => {
    if (opened) {
      setViewDate(initialDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  // ── FHIR data ────────────────────────────────────────────────────
  const orgId = useOrgId();
  const { sites, roomsBySiteId, equipmentBySiteId, loading } = useLocationResources({ organizationId: orgId });

  // Internal site selection — FHIR Location UUID
  const [selectedSiteId, setSelectedSiteId] = useState('');

  // Sync when drawer opens or sites load: prefer a site whose ID matches the
  // incoming locationId prop (which may be a FHIR UUID after first selection),
  // otherwise fall back to the first available site.
  useEffect(() => {
    if (!opened || sites.length === 0) return;
    const match = sites.find((s) => s.id === locationId);
    setSelectedSiteId(match?.id ?? sites[0].id ?? '');
  }, [opened, sites, locationId]);

  const siteOptions = useMemo(
    () =>
      sites.map((s) => ({
        value: s.id,
        label: [s.name, s.addressLine1].filter(Boolean).join(' — '),
      })),
    [sites],
  );

  const roomsAtLocation = useMemo(
    () => (roomsBySiteId[selectedSiteId] ?? []).filter((room) => room.status === 'active'),
    [roomsBySiteId, selectedSiteId],
  );

  const portableDevices = useMemo(
    () =>
      (equipmentBySiteId[selectedSiteId] ?? []).filter(
        (e) => e.portability === 'portable' && e.status === 'active',
      ),
    [equipmentBySiteId, selectedSiteId],
  );

  const grouped = useMemo(() => groupRooms(roomsAtLocation), [roomsAtLocation]);
  const {
    busyByRoom,
    busyByDevice,
    loading: availabilityLoading,
    error: availabilityError,
  } = useRoomAvailabilityForDate({
    date: viewDate,
    roomIds: roomsAtLocation.map((room) => room.id),
    deviceIds: portableDevices.map((device) => device.id),
    refreshKey: opened,
  });
  const bookingsByRoom = useMemo(
    () => new Map(
      roomsAtLocation.map((room) => [room.id, intervalsToBookings(room.id, busyByRoom.get(room.id) ?? [])]),
    ),
    [roomsAtLocation, busyByRoom],
  );
  const bookingsByDevice = useMemo(
    () => new Map(
      portableDevices.map((device) => [
        device.id,
        intervalsToBookings(device.id, busyByDevice.get(device.id) ?? []),
      ]),
    ),
    [portableDevices, busyByDevice],
  );

  // ── Highlight / conflict ─────────────────────────────────────────
  const viewInstant = applyZonedWallClock(viewDate, 12, 0, DEFAULT_PROVIDER_WEEKLY_AVAILABILITY_TIMEZONE);
  const siteHours = sites.find((s) => s.id === selectedSiteId)?.hours;
  const sameDayHighlight = Boolean(
    highlightStart &&
    highlightEnd &&
    highlightEnd > highlightStart &&
    denverDateKey(highlightStart) === denverDateKey(viewInstant),
  );

  const highlightStartMin = sameDayHighlight && highlightStart
    ? getZonedWeekdayAndMinutes(highlightStart.toISOString())?.minutes ?? null
    : null;
  const highlightEndMin = sameDayHighlight && highlightEnd
    ? (() => {
        const z = getZonedWeekdayAndMinutes(highlightEnd.toISOString());
        if (!z) return null;
        const startZ = highlightStart
          ? getZonedWeekdayAndMinutes(highlightStart.toISOString())
          : undefined;
        if (startZ && z.day !== startZ.day && z.minutes === 0) return 24 * 60;
        return z.minutes;
      })()
    : null;

  const hourTicks = useMemo(() => {
    const ticks: number[] = [];
    for (let m = TIMELINE_START_MIN; m <= TIMELINE_END_MIN; m += 60) {
      ticks.push(m);
    }
    return ticks;
  }, []);

  const handleRowClick = (room: Room) => {
    if (!sameDayHighlight || highlightStartMin === null || highlightEndMin === null) return;
    if (highlightStart && highlightEnd && !isRoomOpenForBooking(room, siteHours, highlightStart, highlightEnd)) {
      return;
    }
    const roomBookings = bookingsByRoom.get(room.id) ?? [];
    const conflicts = roomBookings.some(
      (b) => highlightStartMin < b.endMinutes && highlightEndMin > b.startMinutes,
    );
    if (conflicts) return;
    onPickRoom(room.id);
    onClose();
  };

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      title={
        <Group gap="md" justify="space-between" align="center" style={{ width: '100%' }}>
          <Text fw={700} size="md">Room availability</Text>
          <Tooltip label="Toggle free/busy view" position="left" withArrow>
            <Switch
              size="sm"
              checked={drawerToggle}
              onChange={(e) => setDrawerToggle(e.currentTarget.checked)}
            />
          </Tooltip>
        </Group>
      }
      position="right"
      size={820}
      padding="md"
      styles={{ content: { border: '1px solid var(--mantine-color-gray-5)' }, title: { flex: 1 } }}
    >
      <Group justify="space-between" align="center" wrap="wrap" gap="md" mb="md">
        <TimelineColorLegend variant="full" inverted={drawerToggle} />
        <Group justify="center" gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 200 }}>
          <ActionIcon
            variant="default"
            onClick={() => {
              const d = new Date(viewDate);
              d.setDate(d.getDate() - 1);
              setViewDate(d);
            }}
            aria-label="Previous day"
          >
            <IconChevronLeft size={18} />
          </ActionIcon>
          <Text fw={600} size="sm" style={{ minWidth: 168, textAlign: 'center' }}>
            {viewDate.toLocaleDateString('en-US', {
              weekday: 'short',
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </Text>
          <ActionIcon
            variant="default"
            onClick={() => {
              const d = new Date(viewDate);
              d.setDate(d.getDate() + 1);
              setViewDate(d);
            }}
            aria-label="Next day"
          >
            <IconChevronRight size={18} />
          </ActionIcon>
        </Group>
        <Select
          data={siteOptions}
          value={selectedSiteId}
          onChange={(v) => {
            const id = v ?? '';
            setSelectedSiteId(id);
            onOfficeLocationChange?.(id);
          }}
          disabled={officeSelectDisabled || siteOptions.length === 0}
          size="sm"
          w={260}
          aria-label="Office location"
          placeholder={loading ? 'Loading…' : 'No locations'}
        />
      </Group>

      {loading || availabilityLoading ? (
        <Group justify="center" mt="xl">
          <Loader size="sm" />
        </Group>
      ) : roomsAtLocation.length === 0 && portableDevices.length === 0 ? (
        <Text size="sm" c="dimmed">
          {sites.length === 0
            ? 'No practice locations configured yet.'
            : 'No rooms or equipment at this location.'}
        </Text>
      ) : (
        <Box mah="calc(100vh - 220px)" style={{ overflowY: 'auto', overflowX: 'hidden' }}>
          <Stack gap="xl" pr="sm">
            {availabilityError && (
              <Text size="sm" c="red">
                Could not load room availability: {availabilityError}
              </Text>
            )}
            <Group wrap="nowrap" gap={8} align="flex-start">
              <Box w={120} />
              <Box style={{ flex: 1, position: 'relative', height: 22 }}>
                {hourTicks.map((hm) => (
                  <Text
                    key={hm}
                    size="xs"
                    c="dimmed"
                    style={{
                      position: 'absolute',
                      left: `${((hm - TIMELINE_START_MIN) / TOTAL_MIN) * 100}%`,
                      transform: 'translateX(-50%)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {formatHour(hm)}
                  </Text>
                ))}
              </Box>
            </Group>

            {grouped.length === 0 && (
              <Stack gap="xs">
                <Text size="xs" fw={700} c="dimmed" tt="uppercase">Rooms</Text>
                <Text size="sm" c="dimmed">No rooms at this location.</Text>
              </Stack>
            )}

            {grouped.map((section) => (
              <Stack key={section.label} gap="xs">
                <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                  {section.label}
                </Text>
                {section.rooms.map((room) => {
                  const roomBookings = bookingsByRoom.get(room.id) ?? [];
                  const hoursClosed =
                    sameDayHighlight &&
                    highlightStart &&
                    highlightEnd &&
                    !isRoomOpenForBooking(room, siteHours, highlightStart, highlightEnd);
                  const rowBusy =
                    sameDayHighlight &&
                    highlightStartMin !== null &&
                    highlightEndMin !== null &&
                    roomBookings.some(
                      (b) => highlightStartMin < b.endMinutes && highlightEndMin > b.startMinutes,
                    );
                  const rowBlocked = Boolean(hoursClosed || rowBusy);
                  const effectiveHours = resolveEffectiveWeeklyHours(siteHours, room.hours);
                  return (
                    <Group key={room.id} align="stretch" wrap="nowrap" gap={8}>
                      <Box w={120} pt={6}>
                        <Tooltip
                          label={
                            !sameDayHighlight
                              ? 'Navigate to your appointment date to select a room'
                              : hoursClosed
                                ? 'Closed for your selected time'
                                : rowBusy
                                  ? 'Booked for your selected time'
                                  : 'Click to choose this room'
                          }
                        >
                          <Box
                            component="button"
                            type="button"
                            onClick={() => handleRowClick(room)}
                            style={{
                              background: 'none',
                              border: 'none',
                              padding: 0,
                              cursor: rowBlocked || !sameDayHighlight ? 'default' : 'pointer',
                              textAlign: 'left',
                              width: '100%',
                            }}
                          >
                            <Text
                              size="sm"
                              fw={500}
                              lineClamp={2}
                              c={room.status === 'inactive' ? 'dimmed' : undefined}
                            >
                              {room.name}
                            </Text>
                            {room.status === 'inactive' && (
                              <Badge size="xs" color="gray" variant="outline" mt={4}>
                                Inactive
                              </Badge>
                            )}
                            {hoursClosed && (
                              <Badge size="xs" color="gray" variant="light" mt={4}>
                                Closed
                              </Badge>
                            )}
                            {selectedRoomId === room.id && (
                              <Badge size="xs" color="gold" variant="light" mt={4}>
                                Selected
                              </Badge>
                            )}
                          </Box>
                        </Tooltip>
                      </Box>
                      <ResourceTimelineRow
                        bookings={roomBookings}
                        inverted={drawerToggle}
                        closedIntervals={closedIntervalsForHours(effectiveHours, viewInstant)}
                        appointmentHighlight={
                          sameDayHighlight &&
                          highlightStartMin !== null &&
                          highlightEndMin !== null &&
                          highlightEndMin > highlightStartMin
                            ? {
                                startMin: highlightStartMin,
                                endMin: highlightEndMin,
                                conflict: Boolean(rowBlocked),
                              }
                            : null
                        }
                      />
                    </Group>
                  );
                })}
              </Stack>
            ))}

            {portableDevices.length > 0 && (
              <>
                <Divider my="lg" color="gray.3" />
                <Stack gap="xs">
                  <Text size="xs" fw={700} c="dimmed" tt="uppercase">
                    Equipment
                  </Text>
                  <Text size="xs" c="dimmed">
                    Portable devices — reservations follow the device. Fixed equipment stays with its room above.
                  </Text>
                  {portableDevices.map((dev) => {
                    const devBookings = bookingsByDevice.get(dev.id) ?? [];
                    return (
                      <Group key={dev.id} align="stretch" wrap="nowrap" gap={8}>
                        <Box w={120} pt={6}>
                          <Text size="sm" fw={500} lineClamp={2}>
                            {dev.name}
                          </Text>
                          {selectedDeviceIds.includes(dev.id) && (
                            <Badge size="xs" color="gold" variant="light" mt={4}>
                              Selected
                            </Badge>
                          )}
                        </Box>
                        <ResourceTimelineRow
                          bookings={devBookings}
                          inverted={drawerToggle}
                          appointmentHighlight={
                            sameDayHighlight &&
                            highlightStartMin !== null &&
                            highlightEndMin !== null &&
                            highlightEndMin > highlightStartMin
                              ? {
                                  startMin: highlightStartMin,
                                  endMin: highlightEndMin,
                                  conflict: devBookings.some(
                                    (b) =>
                                      highlightStartMin < b.endMinutes &&
                                      highlightEndMin > b.startMinutes,
                                  ),
                                }
                              : null
                          }
                        />
                      </Group>
                    );
                  })}
                </Stack>
              </>
            )}
          </Stack>
        </Box>
      )}
    </Drawer>
  );
}

// ─── Browse mode — opened from the calendar toolbar "Rooms" button ───

const DRAWER_WIDTH = 820;

export interface RoomBrowseDrawerProps {
  opened: boolean;
  onClose: () => void;
}

export function RoomBrowseDrawer({ opened, onClose }: RoomBrowseDrawerProps): JSX.Element {
  const [viewDate, setViewDate] = useState(() => new Date());
  const [drawerToggle, setDrawerToggle] = useState(false);

  useEffect(() => {
    if (opened) {
      setViewDate(new Date());
    }
  }, [opened]);

  // ── FHIR data ────────────────────────────────────────────────────
  const orgId = useOrgId();
  const { sites, roomsBySiteId, equipmentBySiteId, loading } = useLocationResources({ organizationId: orgId });

  const [selectedSiteId, setSelectedSiteId] = useState('');

  useEffect(() => {
    if (sites.length > 0 && !selectedSiteId) {
      setSelectedSiteId(sites[0].id);
    }
  }, [sites, selectedSiteId]);

  const siteOptions = useMemo(
    () =>
      sites.map((s) => ({
        value: s.id,
        label: [s.name, s.addressLine1].filter(Boolean).join(' — '),
      })),
    [sites],
  );

  const roomsAtLocation = useMemo(
    () => (roomsBySiteId[selectedSiteId] ?? []).filter((room) => room.status === 'active'),
    [roomsBySiteId, selectedSiteId],
  );

  const portableDevices = useMemo(
    () =>
      (equipmentBySiteId[selectedSiteId] ?? []).filter(
        (e) => e.portability === 'portable' && e.status === 'active',
      ),
    [equipmentBySiteId, selectedSiteId],
  );

  const grouped = useMemo(() => groupRooms(roomsAtLocation), [roomsAtLocation]);
  const {
    busyByRoom,
    busyByDevice,
    loading: availabilityLoading,
    error: availabilityError,
  } = useRoomAvailabilityForDate({
    date: viewDate,
    roomIds: roomsAtLocation.map((room) => room.id),
    deviceIds: portableDevices.map((device) => device.id),
    refreshKey: opened,
  });
  const bookingsByRoom = useMemo(
    () => new Map(
      roomsAtLocation.map((room) => [room.id, intervalsToBookings(room.id, busyByRoom.get(room.id) ?? [])]),
    ),
    [roomsAtLocation, busyByRoom],
  );
  const bookingsByDevice = useMemo(
    () => new Map(
      portableDevices.map((device) => [
        device.id,
        intervalsToBookings(device.id, busyByDevice.get(device.id) ?? []),
      ]),
    ),
    [portableDevices, busyByDevice],
  );

  const viewInstant = applyZonedWallClock(
    viewDate,
    12,
    0,
    DEFAULT_PROVIDER_WEEKLY_AVAILABILITY_TIMEZONE,
  );
  const siteHours = sites.find((s) => s.id === selectedSiteId)?.hours;

  const browseHourTicks = useMemo(() => {
    const ticks: number[] = [];
    for (let m = TIMELINE_START_MIN; m <= TIMELINE_END_MIN; m += 60) {
      ticks.push(m);
    }
    return ticks;
  }, []);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={DRAWER_WIDTH}
      padding="md"
      styles={{ content: { border: '1px solid var(--mantine-color-gray-5)' }, title: { flex: 1 } }}
      title={
        <Group gap="md" justify="space-between" align="center" style={{ width: '100%' }}>
          <Text fw={700} size="md">Room availability</Text>
          <Tooltip label="Toggle free/busy view" position="left" withArrow>
            <Switch
              size="sm"
              checked={drawerToggle}
              onChange={(e) => setDrawerToggle(e.currentTarget.checked)}
            />
          </Tooltip>
        </Group>
      }
    >
      <Stack gap="md">
        <Group justify="space-between" align="center" wrap="wrap" gap="md">
          <TimelineColorLegend variant="browse" inverted={drawerToggle} />
          <Group justify="center" gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 200 }}>
            <ActionIcon
              variant="default"
              onClick={() => {
                const d = new Date(viewDate);
                d.setDate(d.getDate() - 1);
                setViewDate(d);
              }}
              aria-label="Previous day"
            >
              <IconChevronLeft size={18} />
            </ActionIcon>
            <Text fw={600} size="sm" style={{ minWidth: 168, textAlign: 'center' }}>
              {viewDate.toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
                year: 'numeric',
              })}
            </Text>
            <ActionIcon
              variant="default"
              onClick={() => {
                const d = new Date(viewDate);
                d.setDate(d.getDate() + 1);
                setViewDate(d);
              }}
              aria-label="Next day"
            >
              <IconChevronRight size={18} />
            </ActionIcon>
          </Group>
          <Select
            data={siteOptions}
            value={selectedSiteId}
            onChange={(v) => setSelectedSiteId(v ?? '')}
            size="sm"
            w={260}
            aria-label="Office location"
            placeholder={loading ? 'Loading…' : 'No locations'}
          />
        </Group>

        {loading || availabilityLoading ? (
          <Group justify="center" mt="xl">
            <Loader size="sm" />
          </Group>
        ) : roomsAtLocation.length === 0 && portableDevices.length === 0 ? (
          <Text size="sm" c="dimmed" mt="lg">
            {sites.length === 0
              ? 'No practice locations configured yet.'
              : 'No rooms or equipment at this location.'}
          </Text>
        ) : (
          <Box mah="calc(100vh - 220px)" style={{ overflowY: 'auto', overflowX: 'hidden' }}>
            <Stack gap="xl" pr="sm">
              {availabilityError && (
                <Text size="sm" c="red">
                  Could not load room availability: {availabilityError}
                </Text>
              )}
              <Group wrap="nowrap" gap={8} align="flex-start">
                <Box w={120} />
                <Box style={{ flex: 1, position: 'relative', height: 22 }}>
                  {browseHourTicks.map((hm) => (
                    <Text
                      key={hm}
                      size="xs"
                      c="dimmed"
                      style={{
                        position: 'absolute',
                        left: `${((hm - TIMELINE_START_MIN) / TOTAL_MIN) * 100}%`,
                        transform: 'translateX(-50%)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {formatHour(hm)}
                    </Text>
                  ))}
                </Box>
              </Group>

              {grouped.length === 0 && (
                <Stack gap="xs">
                  <Text size="xs" fw={700} c="dimmed" tt="uppercase">Rooms</Text>
                  <Text size="sm" c="dimmed">No rooms at this location.</Text>
                </Stack>
              )}

              {grouped.map((section) => (
                <Stack key={section.label} gap="xs">
                  <Text size="xs" fw={700} c="dimmed" tt="uppercase">{section.label}</Text>
                  {section.rooms.map((room) => {
                    const roomBookings = bookingsByRoom.get(room.id) ?? [];
                    return (
                      <Group key={room.id} align="stretch" wrap="nowrap" gap={8}>
                        <Box w={120} pt={6}>
                          <Text
                            size="sm"
                            fw={500}
                            lineClamp={2}
                            c={room.status === 'inactive' ? 'dimmed' : undefined}
                          >
                            {room.name}
                          </Text>
                          {room.status === 'inactive' && (
                            <Badge size="xs" color="gray" variant="outline" mt={4}>
                              Inactive
                            </Badge>
                          )}
                        </Box>
                        <ResourceTimelineRow
                          bookings={roomBookings}
                          inverted={drawerToggle}
                          closedIntervals={closedIntervalsForHours(
                            resolveEffectiveWeeklyHours(siteHours, room.hours),
                            viewInstant,
                          )}
                        />
                      </Group>
                    );
                  })}
                </Stack>
              ))}

              {portableDevices.length > 0 && (
                <>
                  <Divider my="lg" color="gray.3" />
                  <Stack gap="xs">
                    <Text size="xs" fw={700} c="dimmed" tt="uppercase">Equipment</Text>
                    <Text size="xs" c="dimmed">
                      Portable devices — reservations follow the device. Fixed equipment stays with its room above.
                    </Text>
                    {portableDevices.map((dev) => {
                      const devBookings = bookingsByDevice.get(dev.id) ?? [];
                      return (
                        <Group key={dev.id} align="stretch" wrap="nowrap" gap={8}>
                          <Box w={120} pt={6}>
                            <Text size="sm" fw={500} lineClamp={2}>{dev.name}</Text>
                          </Box>
                          <ResourceTimelineRow bookings={devBookings} inverted={drawerToggle} />
                        </Group>
                      );
                    })}
                  </Stack>
                </>
              )}
            </Stack>
          </Box>
        )}
      </Stack>
    </Drawer>
  );
}
